import React, { useCallback, useEffect, useMemo, useState } from 'react';
import http from '../services/httpClient';
import './ClosingDatePicker.css';

/**
 * Date-first calendar for Closing Stock (Continuous Closing Stock spec §4.8).
 *
 * "Which day's closing stock is this?" is answered BEFORE any item is entered, and the
 * draft, the count and everything it records carry that date. The page used to file every
 * count under the day it was typed, so a month-end count typed on the 5th was saved as the
 * 5th and had to be moved back by hand.
 *
 * The rules live on the server (GET /closing-stock/calendar, services/ccs/calendar.js);
 * this component only shows them: future days and days before the last confirmed count
 * cannot be picked, a dot marks a day that already holds a draft or a count, more than
 * seven days back asks for a manager, and "Counted on a later day" sends the real counting
 * day so the app can adjust for what moved in between.
 *
 * Dates are plain 'YYYY-MM-DD' strings throughout; all arithmetic is in UTC so no time
 * zone can shift a day.
 */

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const toUtc = (iso) => new Date(`${iso}T00:00:00Z`);
const isoOf = (d) => d.toISOString().slice(0, 10);
const addDays = (iso, n) => { const d = toUtc(iso); d.setUTCDate(d.getUTCDate() + n); return isoOf(d); };
const daysBetween = (from, to) => Math.round((toUtc(to) - toUtc(from)) / 86400000);
const lastDayOfPreviousMonth = (iso) => { const d = toUtc(`${iso.slice(0, 8)}01`); d.setUTCDate(0); return isoOf(d); };
const shiftMonth = (month, n) => {
  const d = toUtc(`${month}-01`);
  d.setUTCMonth(d.getUTCMonth() + n);
  return isoOf(d).slice(0, 7);
};
const isLastDayOfMonth = (iso) => addDays(iso, 1).slice(8, 10) === '01';

export const formatLongDate = (iso) => {
  if (!iso) return '';
  const d = toUtc(iso);
  return `${d.getUTCDate()} ${SHORT_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

/**
 * What the count will be read as. Month-end reports attribute a count dated in the first
 * seven days of a month to the PREVIOUS month (utils/eomPeriod), so the line says so.
 */
export const savingAsLine = (iso) => {
  if (!iso) return '';
  const d = toUtc(iso);
  if (d.getUTCDate() <= 7) {
    const prev = toUtc(lastDayOfPreviousMonth(iso));
    return `Saving as ${MONTHS[prev.getUTCMonth()]} closing stock, ${formatLongDate(iso)}`;
  }
  if (isLastDayOfMonth(iso)) return `Saving as ${MONTHS[d.getUTCMonth()]} closing stock, ${formatLongDate(iso)}`;
  return `Saving as closing stock for ${formatLongDate(iso)}`;
};

function monthGrid(month) {
  const first = toUtc(`${month}-01`);
  const lead = (first.getUTCDay() + 6) % 7; // Monday-first
  const cells = [];
  for (let i = 0; i < lead; i += 1) cells.push(null);
  for (let d = first; d.getUTCMonth() === first.getUTCMonth(); d = toUtc(addDays(isoOf(d), 1))) {
    cells.push(isoOf(d));
  }
  return cells;
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {string|null} props.currentDate        the date the page is on now (null on first open)
 * @param {string|null} props.currentCountedDate
 * @param {boolean} props.hasWork                entries exist on the current date (in memory or a saved draft)
 * @param {boolean} props.firstOpen              true when the page has not chosen a date yet
 * @param {(choice: { closingDate: string, countedDate: string, managerPin: string|null,
 *                    movedDraft: boolean }) => void} props.onConfirm
 * @param {() => void} props.onCancel
 */
export default function ClosingDatePicker({
  open, currentDate, currentCountedDate, hasWork, firstOpen, onConfirm, onCancel,
}) {
  const [cal, setCal] = useState(null);
  const [month, setMonth] = useState(null);
  const [selected, setSelected] = useState(null);
  const [laterDay, setLaterDay] = useState(false);
  const [countedDate, setCountedDate] = useState('');
  const [managerPin, setManagerPin] = useState('');
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [askMove, setAskMove] = useState(null); // { from, to, mode: 'current' | 'latest' }
  const [moveError, setMoveError] = useState('');

  const load = useCallback(async (m) => {
    setLoadError('');
    try {
      const res = await http.get(`/closing-stock/calendar${m ? `?month=${m}` : ''}`);
      if (res && res.success) {
        setCal(res);
        return res;
      }
      setLoadError((res && res.error) || 'Could not load the calendar.');
    } catch (e) {
      setLoadError((e && e.message) || 'Could not load the calendar.');
    }
    return null;
  }, []);

  // Opening: land on the date the page is on, else the latest draft (spec: resuming a
  // draft opens on that draft's date), else the default (end of last month in the first
  // week of a month, otherwise today).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setAskMove(null);
    setMoveError('');
    setManagerPin('');
    (async () => {
      const res = await load(null);
      if (cancelled || !res) return;
      const start = currentDate
        || (firstOpen && res.latestDraft ? res.latestDraft.date : null)
        || res.defaultDate;
      setSelected(start);
      setMonth(start.slice(0, 7));
      const later = currentCountedDate && currentDate && currentCountedDate > currentDate;
      setLaterDay(Boolean(later));
      setCountedDate(later ? currentCountedDate : '');
      if (start.slice(0, 7) !== res.month) load(start.slice(0, 7));
    })();
    return () => { cancelled = true; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const goMonth = (n) => {
    const next = shiftMonth(month, n);
    setMonth(next);
    load(next);
  };

  const today = cal ? cal.today : null;
  const minDate = cal ? cal.minDate : null;

  const isDisabled = useCallback((iso) => {
    if (!cal) return true;
    if (iso > cal.today) return true;
    if (minDate && iso < minDate) return true;
    return (cal.lockedMonths || []).includes(iso.slice(0, 7));
  }, [cal, minDate]);

  const dotDates = useMemo(() => {
    const s = new Set();
    if (!cal) return s;
    (cal.draftDates || []).forEach((d) => s.add(d));
    (cal.counts || []).forEach((c) => s.add(c.date));
    return s;
  }, [cal]);

  const existingCount = cal && selected ? (cal.counts || []).find((c) => c.date === selected && !c.weekly) : null;
  const needsManager = Boolean(today && selected && daysBetween(selected, today) > (cal ? cal.managerAfterDays : 7));
  const draftOnSelected = Boolean(cal && selected && (cal.draftDates || []).includes(selected));

  const pick = (iso) => {
    if (!iso || isDisabled(iso)) return;
    setSelected(iso);
    setAskMove(null);
    setMoveError('');
    if (laterDay && countedDate && countedDate <= iso) setCountedDate('');
    if (iso.slice(0, 7) !== month) { setMonth(iso.slice(0, 7)); load(iso.slice(0, 7)); }
  };

  const countedValid = !laterDay || (countedDate && countedDate > selected && today && countedDate <= today);
  const pinValid = !needsManager || managerPin === '' || /^\d{4}$/.test(managerPin);
  const canContinue = Boolean(selected && !isDisabled(selected) && countedValid && pinValid && !busy);

  const finish = (movedDraft) => {
    onConfirm({
      closingDate: selected,
      countedDate: laterDay ? countedDate : selected,
      managerPin: needsManager && managerPin ? managerPin : null,
      movedDraft,
    });
  };

  const moveDraft = async (from, to) => {
    setBusy(true);
    setMoveError('');
    try {
      const res = await http.post('/closing-stock/draft/move', { from, to });
      if (res && res.success) {
        finish(true);
        return;
      }
      setMoveError((res && res.error) || 'The draft could not be moved.');
    } catch (e) {
      // Nothing saved under the old date yet: the entries live only on this screen and
      // simply come along to the new date.
      if (e && e.data && e.data.code === 'NO_DRAFT') {
        finish(false);
        return;
      }
      setMoveError((e && e.data && e.data.error) || (e && e.message) || 'The draft could not be moved.');
    } finally {
      setBusy(false);
    }
  };

  const handleContinue = () => {
    if (!canContinue) return;
    // Changing the date of a count already under way: the draft moves with it — never
    // copied, never left behind (spec §4.8).
    if (!firstOpen && currentDate && selected !== currentDate && hasWork) {
      setAskMove({ from: currentDate, to: selected, mode: 'current' });
      return;
    }
    // First open on a date other than the draft we would have resumed (spec T11: an old
    // draft saved under the 5th that belongs to 31 Oct).
    if (firstOpen && cal && cal.latestDraft && cal.latestDraft.date !== selected && !draftOnSelected) {
      setAskMove({ from: cal.latestDraft.date, to: selected, mode: 'latest' });
      return;
    }
    finish(false);
  };

  const confirmMove = async () => {
    if (!askMove) return;
    await moveDraft(askMove.from, askMove.to);
  };

  if (!open) return null;

  const endOfLastMonth = today ? lastDayOfPreviousMonth(today) : null;
  const quick = [
    { key: 'eolm', label: 'End of last month', date: endOfLastMonth },
    { key: 'today', label: 'Today', date: today },
  ];

  return (
    <div className="cs-modal-overlay cdp-overlay" role="dialog" aria-modal="true" aria-labelledby="cdp-title">
      <div className="cdp-modal">
        <div className="cdp-title" id="cdp-title">Which day&apos;s closing stock is this?</div>
        <div className="cdp-subtitle">Drafts and the count are saved under this date.</div>

        {loadError && <div className="cdp-error">{loadError}</div>}

        <div className="cdp-quick">
          {quick.map((q) => (
            <button
              key={q.key}
              type="button"
              className={`cdp-chip${selected && q.date === selected ? ' cdp-chip-on' : ''}`}
              onClick={() => pick(q.date)}
              disabled={!q.date || isDisabled(q.date)}
            >
              {q.label}
            </button>
          ))}
          <button
            type="button"
            className={`cdp-chip${selected && selected !== endOfLastMonth && selected !== today ? ' cdp-chip-on' : ''}`}
            onClick={() => { const el = document.getElementById('cdp-grid'); if (el) el.focus(); }}
          >
            Pick a date
          </button>
        </div>

        {month && (
          <div className="cdp-calendar">
            <div className="cdp-month-bar">
              <button type="button" className="cdp-nav" onClick={() => goMonth(-1)} aria-label="Previous month">‹</button>
              <div className="cdp-month">{MONTHS[Number(month.slice(5, 7)) - 1]} {month.slice(0, 4)}</div>
              <button
                type="button"
                className="cdp-nav"
                onClick={() => goMonth(1)}
                disabled={today && month >= today.slice(0, 7)}
                aria-label="Next month"
              >
                ›
              </button>
            </div>
            <div className="cdp-grid" id="cdp-grid" tabIndex={-1}>
              {WEEKDAYS.map((w) => <div key={w} className="cdp-weekday">{w}</div>)}
              {monthGrid(month).map((iso, i) => (iso
                ? (
                  <button
                    key={iso}
                    type="button"
                    className={`cdp-day${iso === selected ? ' cdp-day-on' : ''}${iso === today ? ' cdp-day-today' : ''}`}
                    disabled={isDisabled(iso)}
                    onClick={() => pick(iso)}
                    aria-label={formatLongDate(iso)}
                    aria-pressed={iso === selected}
                  >
                    {Number(iso.slice(8, 10))}
                    {dotDates.has(iso) && <span className="cdp-dot" aria-hidden="true" />}
                  </button>
                )
                : <div key={`blank-${i}`} />))}
            </div>
          </div>
        )}

        <div className="cdp-legend">
          <span className="cdp-dot cdp-dot-inline" aria-hidden="true" /> A draft or a submitted count exists on this date
        </div>
        <div className="cdp-note">
          Future dates{minDate ? ` and dates before the last count (${formatLongDate(minDate)}${cal.lastConfirmedCount?.by ? `, by ${cal.lastConfirmedCount.by}` : ''})` : ''} cannot be picked
        </div>

        {existingCount && (
          <div className="cdp-warn">
            This will replace the count submitted{existingCount.by ? ` by ${existingCount.by}` : ''} on {formatLongDate(existingCount.date)}. The old one is kept in history as replaced.
          </div>
        )}

        {needsManager && (
          <div className="cdp-warn">
            This is more than {cal ? cal.managerAfterDays : 7} days back, so a manager or the owner must apply it.
            <label className="cdp-pin-label">
              Not a manager? Ask one to enter the manager PIN:
              <input
                className="cdp-pin"
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={managerPin}
                onChange={(e) => setManagerPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="••••"
                aria-label="Manager PIN"
              />
            </label>
          </div>
        )}

        <label className="cdp-later">
          <input
            type="checkbox"
            checked={laterDay}
            onChange={(e) => { setLaterDay(e.target.checked); if (!e.target.checked) setCountedDate(''); }}
          />
          <span>Counted on a later day (asks for the count date)</span>
        </label>
        {laterDay && selected && (
          <div className="cdp-later-row">
            <span>Shelves counted on</span>
            <input
              type="date"
              className="cdp-date-input"
              value={countedDate}
              min={addDays(selected, 1)}
              max={today || undefined}
              onChange={(e) => setCountedDate(e.target.value)}
            />
            {!countedValid && <span className="cdp-hint">Pick a day after {formatLongDate(selected)}, up to today.</span>}
            {countedValid && countedDate && (
              <span className="cdp-hint">Sales and deliveries between the two days will be adjusted.</span>
            )}
          </div>
        )}

        {selected && <div className="cdp-saving">{savingAsLine(selected)}</div>}

        {askMove && (
          <div className="cdp-move">
            <div className="cdp-move-text">
              {askMove.mode === 'current'
                ? `Move this count to ${formatLongDate(askMove.to)}?`
                : `You have a draft saved for ${formatLongDate(askMove.from)}. Move it to ${formatLongDate(askMove.to)}?`}
            </div>
            {moveError && <div className="cdp-error">{moveError}</div>}
            <div className="cdp-move-actions">
              <button type="button" className="cs-modal-cancel" onClick={() => (askMove.mode === 'latest' ? finish(false) : setAskMove(null))} disabled={busy}>
                {askMove.mode === 'latest' ? 'No, start a new count' : 'Keep the old date'}
              </button>
              <button type="button" className="cs-modal-confirm" onClick={confirmMove} disabled={busy}>
                {busy ? 'Moving…' : 'Move the draft'}
              </button>
            </div>
          </div>
        )}

        {!askMove && (
          <div className="cs-modal-actions cdp-actions">
            <button type="button" className="cs-modal-cancel" onClick={onCancel}>Cancel</button>
            <button type="button" className="cs-modal-confirm" onClick={handleContinue} disabled={!canContinue}>
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
